/**
 * CommandPalette - Story 24.47
 * Keyboard-first navigation for power users (Cmd+K / Ctrl+K)
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { analyticsService } from '../../services/analytics.service';
import { useAuth } from '../../hooks/useAuth';
import { hasPermission, type PermissionAction } from '../../types/roles';
import {
  Search, Command, ArrowRight, Home, Upload, Target, BarChart3,
  Sparkles, Settings, HelpCircle, FileText, Users, Zap, MessageSquare,
  Clock, Star, ChevronRight
} from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  action: () => void;
  category: 'navigation' | 'action' | 'search' | 'recent';
  keywords?: string[];
  shortcut?: string;
  requiredPermission?: PermissionAction;
}

interface CommandPaletteProps {
  customCommands?: CommandItem[];
  onClose?: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  customCommands = [],
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Default commands
  const defaultCommands: CommandItem[] = useMemo(() => [
    // Navigation
    { id: 'home', label: 'Go to Home', icon: Home, action: () => navigate('/dashboard'), category: 'navigation', keywords: ['dashboard', 'main'] },
    { id: 'upload', label: 'Upload Center', icon: Upload, action: () => navigate('/upload-center'), category: 'navigation', keywords: ['files', 'documents'] },
    { id: 'campaigns', label: 'Campaigns', icon: Target, action: () => navigate('/campaigns'), category: 'navigation', keywords: ['marketing', 'projects'] },
    { id: 'templates', label: 'ARIA Templates', icon: Sparkles, action: () => navigate('/templates'), category: 'navigation', keywords: ['aria', 'style'] },
    { id: 'analytics', label: 'Analytics Dashboard', icon: BarChart3, action: () => navigate('/dashboard'), category: 'navigation', keywords: ['metrics', 'stats'] },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      action: () => navigate('/settings'),
      category: 'navigation',
      shortcut: 'Ctrl+,',
      requiredPermission: 'org.edit_settings',
    },
    
    // Actions
    { id: 'new-campaign', label: 'Create New Campaign', icon: Target, action: () => navigate('/workflow/1'), category: 'action', keywords: ['add', 'start'] },
    { id: 'ask-emma', label: 'Ask Emma', icon: MessageSquare, action: () => { document.dispatchEvent(new CustomEvent('open-emma')); }, category: 'action', shortcut: 'Ctrl+E', keywords: ['help', 'ai', 'chat'] },
    { id: 'quick-upload', label: 'Quick Upload', icon: Upload, action: () => navigate('/upload-center'), category: 'action', keywords: ['add', 'document'] },
    { id: 'new-template', label: 'Create Template', icon: Sparkles, action: () => navigate('/templates?action=new'), category: 'action', keywords: ['aria', 'add'] },
    
    // Search
    { id: 'search-content', label: 'Search Content', icon: FileText, action: () => {}, category: 'search', keywords: ['find', 'documents'] },
    { id: 'search-campaigns', label: 'Search Campaigns', icon: Target, action: () => {}, category: 'search', keywords: ['find', 'projects'] },
  ], [navigate]);

  const allCommands = useMemo(() => {
    const mergedCommands = [...defaultCommands, ...customCommands];
    return mergedCommands.filter((command) => {
      if (!command.requiredPermission) {
        return true;
      }
      return Boolean(user && hasPermission(user.role, command.requiredPermission));
    });
  }, [customCommands, defaultCommands, user]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) {
      return allCommands.slice(0, 8); // Show first 8 when no query
    }

    const lowerQuery = query.toLowerCase();
    return allCommands.filter(cmd => {
      const searchText = [
        cmd.label,
        cmd.description,
        ...(cmd.keywords || [])
      ].join(' ').toLowerCase();
      return searchText.includes(lowerQuery);
    }).slice(0, 10);
  }, [query, allCommands]);

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const wasOpen = isOpen;
        setIsOpen(prev => !prev);
        // Track opens (Ctrl+K triggered)
        if (!wasOpen) {
          analyticsService.trackCommandPaletteOpen(true);
        }
      }
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    onClose?.();
  }, [onClose]);

  const executeCommand = useCallback((command: CommandItem) => {
    command.action();
    handleClose();
  }, [handleClose]);

  const handleKeyNavigation = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
        break;
    }
  };

  const getCategoryLabel = (category: CommandItem['category']) => {
    switch (category) {
      case 'navigation': return 'Navigate';
      case 'action': return 'Actions';
      case 'search': return 'Search';
      case 'recent': return 'Recent';
      default: return category;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Palette */}
      <div
        onClick={e => e.stopPropagation()}
        className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyNavigation}
            placeholder="Type a command or search..."
            className="flex-1 text-lg outline-none placeholder-gray-400"
          />
          <kbd className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs font-mono">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto py-2">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No commands found for "{query}"</p>
            </div>
          ) : (
            <>
              {/* Group by category */}
              {['navigation', 'action', 'search', 'recent'].map(category => {
                const categoryCommands = filteredCommands.filter(c => c.category === category);
                if (categoryCommands.length === 0) return null;

                return (
                  <div key={category} className="mb-2">
                    <div className="px-4 py-1 text-xs font-medium text-gray-400 uppercase">
                      {getCategoryLabel(category as CommandItem['category'])}
                    </div>
                    {categoryCommands.map((command, idx) => {
                      const globalIndex = filteredCommands.indexOf(command);
                      const isSelected = globalIndex === selectedIndex;
                      const Icon = command.icon;

                      return (
                        <button
                          key={command.id}
                          onClick={() => executeCommand(command)}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                              {command.label}
                            </div>
                            {command.description && (
                              <div className="text-sm text-gray-500">{command.description}</div>
                            )}
                          </div>
                          {command.shortcut && (
                            <kbd className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs font-mono">
                              {command.shortcut}
                            </kbd>
                          )}
                          <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-blue-400' : 'text-gray-300'}`} />
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↵</kbd>
              to select
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Command className="w-3 h-3" />
            <span>K to toggle</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommandPalette;
